import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { formatCents, parseCents } from '@shared/money';
import { todayIn } from '@shared/time';
import { createJobSchema } from '@shared/schemas/job';
import { createRecurringServiceSchema } from '@shared/schemas/recurring';
import { RECURRENCE_TYPES } from '@shared/constants';
import styles from './NewJobPage.module.css';

/**
 * A service line the owner is building for this job. The unit price is a FROZEN
 * COPY taken from the catalog at the moment it was picked -- changing the
 * catalog later must not alter this job.
 *
 * `priceInput` holds the raw string the owner is typing (e.g. "45.00") so the
 * input stays controlled while they edit. `unitPriceCents` is the parsed
 * integer-cents value used for the total and the POST body.
 */
interface ServiceLine {
  description: string;
  quantity: number;
  unitPriceCents: number;
  priceInput: string;
  serviceId?: string;
}

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

interface Property {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

interface ServiceCatalogItem {
  id: string;
  name: string;
  defaultPriceCents: number;
}

/** A team member as returned by GET /api/v1/team. */
interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface CreatedJob {
  id: string;
  priceCents: number;
}

interface CreatedRecurring {
  id: string;
}

/**
 * The org timezone is read from the session. The session module is mocked in
 * tests, so this reads whatever the mock provides.
 */
function useOrgTimezone(): string {
  const session = useSession();
  const tz =
    (session.user as { organizationTimezone?: string } | null)?.organizationTimezone ?? 'UTC';
  return tz;
}

function customerLabel(c: Customer): string {
  const parts = [c.firstName, c.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : (c.companyName ?? c.email ?? c.id);
}

function propertyLabel(p: Property): string {
  const line2 = [p.city, p.state, p.postalCode].filter(Boolean).join(', ');
  return [p.addressLine1, line2].filter(Boolean).join(', ');
}

export function NewJobPage() {
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();
  const { user } = useSession();
  // Only an OWNER may read GET /team (roles.ts CREW_ALLOWED has no /team), so
  // the assignee select exists only on the owner's screen and its fetch is
  // gated on the role, never on whether data happened to arrive.
  const isOwner = user?.role === 'OWNER';

  // --- Data loading ---
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [teamFailed, setTeamFailed] = useState(false);

  // --- Form state ---
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  /** Additional assignees; excluded from the primary select's options. */
  const [additionalAssigneeIds, setAdditionalAssigneeIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledStartTime, setScheduledStartTime] = useState('');
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState('');
  const [lines, setLines] = useState<ServiceLine[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverPriceCents, setServerPriceCents] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // --- Repeats (recurring) state ---
  // Pre-ticked by ?repeats=1, which is how the Recurring screen links here now
  // that its own form is gone. Without this the link would land on the job form
  // with Repeats OFF and quietly offer the wrong thing.
  const [searchParams] = useSearchParams();
  const [repeats, setRepeats] = useState(searchParams.get('repeats') === '1');
  const [recurrenceType, setRecurrenceType] = useState('WEEKLY');
  const [intervalCount, setIntervalCount] = useState('1');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Default the date to the ORG's today, never the browser's date.
  useEffect(() => {
    setScheduledDate(todayIn(orgTimezone));
  }, [orgTimezone]);

  // Load customers on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingCustomers(true);
    apiGet<Customer[]>('/api/v1/customers')
      .then((data) => {
        if (!cancelled) {
          setCustomers(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCustomers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCustomers(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the service catalog on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingServices(true);
    apiGet<ServiceCatalogItem[]>('/api/v1/services')
      .then((data) => {
        if (!cancelled) {
          setServices(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServices([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingServices(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the team (OWNER only — /team is not in CREW_ALLOWED) for the
  // "Assigned to" select. A failure here must never block creating the job:
  // the select disables and the owner schedules unassigned.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    apiGet<TeamMember[]>('/api/v1/team')
      .then((members) => {
        if (!cancelled) {
          setTeam(members);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTeamFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  // When the customer changes, refetch THAT customer's properties and clear
  // the chosen property. A job whose property belongs to a different customer
  // is rejected by the server with a 400 -- do not let the owner build that
  // request in the first place.
  useEffect(() => {
    if (!customerId) {
      setProperties([]);
      setPropertyId('');
      return;
    }
    let cancelled = false;
    setLoadingProperties(true);
    apiGet<Property[]>(`/api/v1/properties?customerId=${encodeURIComponent(customerId)}`)
      .then((data) => {
        if (!cancelled) {
          setProperties(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProperties([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProperties(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // --- Line item helpers ---
  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { description: '', quantity: 1, unitPriceCents: 0, priceInput: '' },
    ]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (
    index: number,
    field: 'description' | 'quantity' | 'priceInput',
    value: string | number
  ) => {
    setLines((prev) => {
      const copy = [...prev];
      const original = copy[index];
      if (!original) return prev;
      const line: ServiceLine = {
        description: original.description,
        quantity: original.quantity,
        unitPriceCents: original.unitPriceCents,
        priceInput: original.priceInput,
        serviceId: original.serviceId,
      };
      if (field === 'quantity') {
        const n = Number(value);
        line.quantity = Number.isNaN(n) || n < 1 ? 1 : Math.floor(n);
      } else if (field === 'priceInput') {
        // unitPriceCents is INTEGER CENTS. Take dollars from the owner and
        // convert with parseCents. Never a float, never multiply by 100
        // yourself, never toFixed.
        line.priceInput = value as string;
        const parsed = parseCents(value as string);
        line.unitPriceCents = parsed === null ? 0 : parsed;
      } else {
        line.description = value as string;
      }
      copy[index] = line;
      return copy;
    });
  };

  const applyCatalogItem = (index: number, item: ServiceCatalogItem) => {
    setLines((prev) => {
      const copy = [...prev];
      const line: ServiceLine = {
        description: item.name,
        quantity: 1,
        unitPriceCents: item.defaultPriceCents,
        priceInput: (item.defaultPriceCents / 100).toFixed(2),
        serviceId: item.id,
      };
      copy[index] = line;
      return copy;
    });
  };

  // Running total computed from the line values the owner has entered.
  const estimateCents = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.unitPriceCents, 0),
    [lines]
  );

  // A line is invalid if its price is non-numeric or negative.
  const lineErrors = lines.map((line) => {
    if (line.unitPriceCents < 0) {
      return 'Price must not be negative';
    }
    return null;
  });
  const hasLineError = lineErrors.some((e) => e !== null);

  // When Repeats is on, a recurring rule carries exactly ONE service. More than
  // one line is an error that must be surfaced, not silently collapsed.
  const multiLineRepeatsError =
    repeats && lines.length > 1
      ? 'A repeating job carries a single service. Remove the extra lines or turn off Repeats.'
      : null;

  const canSubmit =
    lines.length > 0 &&
    !hasLineError &&
    !!customerId &&
    !!propertyId &&
    !!title.trim() &&
    !!scheduledDate &&
    !multiLineRepeatsError;

  // A ref guards against double-submit: two taps in one tick both run before
  // React re-renders, so a boolean state flag would not catch the second.
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    // Ref guard: the second tap in the same tick sees this true and bails.
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSubmitting(true);
    setServerError(null);
    setServerPriceCents(null);

    try {
      if (repeats) {
        // --- Recurring mode: POST /api/v1/recurring ---
        // The rule carries ONE service. priceCents = unit price x quantity,
        // in integer cents. serviceId is that line's serviceId, if it has one.
        const line = lines[0];
        if (!line) {
          setServerError('Add a service line.');
          return;
        }
        // Validate the recurring-specific fields with the schema so the
        // dayOfWeek / endDate rules are not duplicated by hand. We validate
        // only the recurrence fields (not the full body) because the customer
        // and property IDs are validated by the server.
        const recurrenceCheck = createRecurringServiceSchema.safeParse({
          customerId,
          propertyId,
          name: title.trim(),
          startDate: scheduledDate,
          recurrenceType,
          intervalCount: Number(intervalCount),
          ...(dayOfWeek !== '' ? { dayOfWeek: Number(dayOfWeek) } : {}),
          ...(endDate ? { endDate } : {}),
        });
        if (!recurrenceCheck.success) {
          const first = recurrenceCheck.error.issues[0];
          const msg = first ? first.message : 'Invalid recurring service';
          setServerError(msg);
          return;
        }

        const priceCents = line.quantity * line.unitPriceCents;
        const body = {
          customerId,
          propertyId,
          ...(line.serviceId ? { serviceId: line.serviceId } : {}),
          name: title.trim(),
          startDate: scheduledDate,
          ...(endDate ? { endDate } : {}),
          recurrenceType,
          intervalCount: Number(intervalCount),
          ...(dayOfWeek !== '' ? { dayOfWeek: Number(dayOfWeek) } : {}),
          priceCents,
          estimatedDurationMinutes: estimatedDurationMinutes
            ? Number(estimatedDurationMinutes)
            : undefined,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        };

        const created = await apiPost<CreatedRecurring>('/api/v1/recurring', body);
        navigate(`/recurring/${created.id}`);
      } else {
        // --- Job mode: POST /api/v1/jobs ---
        // z.INPUT, not z.infer. `infer` is the OUTPUT type, where
        // estimatedDurationMinutes has already had its .default(60) applied and
        // is therefore required — so a body that legitimately omits it, and
        // lets the schema supply the default, does not typecheck.
        const body: z.input<typeof createJobSchema> = {
          customerId,
          propertyId,
          title: title.trim(),
          scheduledDate,
          scheduledStartTime: scheduledStartTime || undefined,
          estimatedDurationMinutes: estimatedDurationMinutes
            ? Number(estimatedDurationMinutes)
            : undefined,
          // Only sent when the owner picked someone; an absent key lets the
          // server store NULL rather than the schema rejecting an empty string.
          ...(assignedUserId ? { assignedUserId } : {}),
          ...(additionalAssigneeIds.length > 0 ? { additionalAssigneeIds } : {}),
          services: lines.map((line) => ({
            description: line.description,
            quantity: line.quantity,
            unitPriceCents: line.unitPriceCents,
            ...(line.serviceId ? { serviceId: line.serviceId } : {}),
          })),
        };

        const created = await apiPost<CreatedJob>('/api/v1/jobs', body);
        setServerPriceCents(created.priceCents);
        navigate(`/jobs/${created.id}`);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const showDayOfWeek = recurrenceType === 'WEEKLY' || recurrenceType === 'BIWEEKLY';

  return (
    <main className={styles.main}>
      <h1 className={styles.title}>Schedule a job</h1>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* --- Customer --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Customer</legend>
          <select
            className={styles.select}
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setPropertyId('');
            }}
            aria-label="Customer"
            disabled={loadingCustomers}
          >
            <option value="" disabled>
              {loadingCustomers ? 'Loading customers…' : 'Choose a customer'}
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {customerLabel(c)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Property --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Property</legend>
          <select
            className={styles.select}
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            aria-label="Property"
            disabled={loadingProperties || !customerId}
          >
            <option value="" disabled>
              {loadingProperties
                ? 'Loading properties…'
                : !customerId
                  ? 'Choose a customer first'
                  : 'Choose a property'}
            </option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {propertyLabel(p)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Assigned to (owner only; /team is not crew-readable) --- */}
        {isOwner && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Assigned to</legend>
            <select
              className={styles.select}
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
              aria-label="Assigned to"
              disabled={teamFailed}
            >
              <option value="">Unassigned</option>
              {/* Inactive members cannot take new work — the server's
                  validateAssignedUser refuses them. */}
              {team
                .filter((member) => member.isActive)
                .map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.displayName}
                  </option>
                ))}
            </select>
          </fieldset>
        )}

        {/* --- Also assign (owner only): multi-select of the remaining
             active members. Multiple picks are the crew-of-two case; the
             primary stays the single-select above. --- */}
        {isOwner && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Also assign</legend>
            <select
              className={styles.select}
              multiple
              size={3}
              value={additionalAssigneeIds}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                setAdditionalAssigneeIds(selected);
              }}
              aria-label="Also assign"
              disabled={teamFailed}
            >
              {team
                .filter((member) => member.isActive && member.id !== assignedUserId)
                .map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.displayName}
                  </option>
                ))}
            </select>
          </fieldset>
        )}

        {/* --- Title --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Title</legend>
          <input
            className={styles.input}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Gutter cleaning"
            aria-label="Title"
          />
        </fieldset>

        {/* --- Date & time --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>When</legend>
          <input
            className={styles.input}
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            aria-label={repeats ? 'Starts on' : 'Scheduled date'}
          />
          {!repeats ? (
            <input
              className={styles.input}
              type="time"
              value={scheduledStartTime}
              onChange={(e) => setScheduledStartTime(e.target.value)}
              placeholder="Optional start time"
              aria-label="Start time"
            />
          ) : null}
          <input
            className={styles.input}
            type="number"
            min={1}
            value={estimatedDurationMinutes}
            onChange={(e) => setEstimatedDurationMinutes(e.target.value)}
            placeholder="Optional duration (minutes)"
            aria-label="Estimated duration in minutes"
          />
        </fieldset>

        {/* --- Repeats --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Repeats</legend>
          <label className={styles.repeatsRow}>
            <input
              type="checkbox"
              checked={repeats}
              onChange={(e) => setRepeats(e.target.checked)}
              aria-label="Repeats"
            />
            <span>Repeats</span>
          </label>
        </fieldset>

        {/* --- Frequency fields (only when Repeats is on) --- */}
        {repeats ? (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Frequency</legend>
            <select
              className={styles.select}
              value={recurrenceType}
              onChange={(e) => {
                setRecurrenceType(e.target.value);
                setDayOfWeek('');
              }}
              aria-label="Recurrence type"
            >
              {RECURRENCE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <input
              className={styles.input}
              type="number"
              min={1}
              value={intervalCount}
              onChange={(e) => setIntervalCount(e.target.value)}
              aria-label="Interval"
              placeholder="Every N"
            />
            {showDayOfWeek ? (
              <select
                className={styles.select}
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(e.target.value)}
                aria-label="Day of week"
              >
                <option value="" disabled>
                  Pick a day
                </option>
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </select>
            ) : null}
            <input
              className={styles.input}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="End date"
              placeholder="Optional end date"
            />
            <input
              className={styles.input}
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              aria-label="Notes"
            />
          </fieldset>
        ) : null}

        {/* --- Service lines --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Services</legend>
          {lines.map((line, index) => (
            <div key={index} className={styles.line}>
              <select
                className={styles.select}
                aria-label={`Catalog for line ${index + 1}`}
                onChange={(e) => {
                  const item = services.find((s) => s.id === e.target.value);
                  if (item) {
                    applyCatalogItem(index, item);
                  }
                }}
                value=""
                disabled={loadingServices}
              >
                <option value="" disabled>
                  {loadingServices ? 'Loading…' : 'Pick from catalog'}
                </option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {formatCents(s.defaultPriceCents)}
                  </option>
                ))}
              </select>
              <input
                className={styles.input}
                type="text"
                value={line.description}
                onChange={(e) => updateLine(index, 'description', e.target.value)}
                placeholder="Description"
                aria-label={`Description for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                aria-label={`Quantity for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="text"
                value={line.priceInput}
                onChange={(e) => updateLine(index, 'priceInput', e.target.value)}
                placeholder="Price"
                aria-label={`Unit price for line ${index + 1}`}
              />
              {lineErrors[index] ? (
                <span className={styles.error} role="alert">
                  {lineErrors[index]}
                </span>
              ) : null}
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeLine(index)}
                aria-label={`Remove line ${index + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.addBtn}
            onClick={addLine}
            aria-label="Add a service line"
          >
            Add service
          </button>
        </fieldset>

        {/* --- Multi-line repeats error --- */}
        {multiLineRepeatsError ? (
          <div className={styles.serverError} role="alert">
            {multiLineRepeatsError}
          </div>
        ) : null}

        {/* --- Total --- */}
        <div className={styles.total}>
          <span>Estimated total:</span>
          <strong>{formatCents(estimateCents)}</strong>
          {serverPriceCents !== null && serverPriceCents !== estimateCents && (
            <span className={styles.serverTotal}>(Server: {formatCents(serverPriceCents)})</span>
          )}
        </div>

        {/* --- Server error --- */}
        {serverError ? (
          <div className={styles.serverError} role="alert">
            {serverError}
          </div>
        ) : null}

        {/* --- Submit --- */}
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={!canSubmit || submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Scheduling…' : 'Schedule job'}
        </button>
        {!canSubmit && lines.length === 0 && (
          <p className={styles.hint}>Add at least one service line to schedule the job.</p>
        )}
      </form>
    </main>
  );
}
